import {
  AppMenuItem,
  BuffetCategory,
  BuffetEpgItem,
  Category,
  HomePageWidget,
  Movie,
  MyChannel,
  Promotion,
  ProviderColorScheme,
  ProviderData,
  TvShow,
  UserInfo,
  VoDGenre
} from "@/services/apiService/api.types";
import apiService from "@/services/apiService/apiService";
import storageService from "@/services/storageService/storageService";
import {
  BuffetCategories,
  Categories,
  LocalStorageItems
} from "@/helpers/constants";
import { formatEpgDates } from "@/helpers/utils";

const defaultCategory: Category = {
  id: 100,
  name: Categories.AllChannels,
  channelsCount: 0,
  vodsCount: 0
};

const defaultBuffetCategory: BuffetCategory = {
  id: 1,
  name: BuffetCategories.Music,
  code: "0",
  position: 10,
  created_at: 0,
  updated_at: 0
};

export const defaultProviderData: ProviderData = {
  id: 0,
  status: 0,
  created_at: 0,
  description: "",
  media: null,
  name: "demo",
  updated_at: 0,
  logo_id: undefined,
  logo: null,
  colorScheme: {
    id: 0,
    background: "",
    transparent: "",
    provider_id: 0,
    name: "default",
    accent: "#00de78",
    mainDark: "#381C67",
    mainLight: "#512992",
    media_id: null,
    media: null
  }
};

export const sortedCategories: Record<string, Category> = {
  [Categories.AllChannels]: defaultCategory
};

export const sortedBuffetCategories: Record<string, BuffetCategory> = {
  [BuffetCategories.Home]: defaultBuffetCategory
};

//consists of items that will be stored in LocalStorage, some api calls are merged together and the result is what is actually saved and used in the app
//method param is indicator of whether user specific channel data is updated(currently stored info is automatically outdated)
const fetchCustomChannelList = async (
  userDataUpdated?: boolean
): Promise<MyChannel[]> => {
  const channelList = storageService.getItem<MyChannel[]>(
    LocalStorageItems.CustomChannelList
  );

  if (!channelList || userDataUpdated) {
    // Both of these calls are independent of each other, let them run async.
    // Once both has succeeded we then continue.
    const [{ items: channels }, userData] = await Promise.all([
      apiService.getMyChannels(),
      apiService.getUserData()
    ]);

    userData.forEach((channelConfig) => {
      channels.forEach((channel) => {
        if (channelConfig.id === channel.id) {
          channel.parentalLock = channelConfig.parentalLock;
          channel.canWatch = channelConfig.canWatch;
          channel.isFavorite = channelConfig.isFavorite;
          channel.userOrder = channelConfig.userOrder;
          channel.videoLive = channelConfig.videoLive;
        }
      });
    });

    channels.sort((a, b) => {
      return a.userOrder - b.userOrder;
    });

    storageService.setItem<MyChannel[]>(
      LocalStorageItems.CustomChannelList,
      channels
    );
    return channels;
  }

  return channelList;
};

const fetchSortedCategories = async (
  userDataUpdated?: boolean
): Promise<Record<Categories, Category>> => {
  const categories = storageService.getItem<Record<Categories, Category>>(
    LocalStorageItems.SortedCategories
  );
  if (!categories || userDataUpdated) {
    //automatically update channelList(many pages depend on sorted categories with channel info)
    const channelList = await fetchCustomChannelList(true);
    const { items: categoryList } = await apiService.getCategories();

    categoryList.forEach((category) => {
      sortedCategories[category.name] = category;
    });

    if (!sortedCategories[Categories.Favorites]) {
      sortedCategories[Categories.Favorites] = defaultCategory;
    }
    sortedCategories[Categories.Favorites].channels = [];
    channelList?.forEach((channel) => {
      channel.categories.forEach((category) => {
        if (!sortedCategories[category.name].channels) {
          sortedCategories[category.name].channels = [];
        }
        sortedCategories[category.name].channels?.push(channel);
      });

      if (channel.isFavorite) {
        sortedCategories[Categories.Favorites].channels?.push(channel);
      }
    });

    storageService.setItem(
      LocalStorageItems.SortedCategories,
      sortedCategories
    );
    return sortedCategories;
  }

  return categories;
};

const fetchPromotions = async (): Promise<Promotion[]> => {
  const promotionsList = storageService.getItem<Promotion[]>(
    LocalStorageItems.Promotions
  );

  if (!promotionsList) {
    const { items: promotions } = await apiService.getPromotions();
    storageService.setItem<Promotion[]>(
      LocalStorageItems.Promotions,
      promotions
    );
    return promotions;
  }

  return promotionsList;
};

const fetchSortedBuffetCategories = async (
  date?: string
): Promise<Record<BuffetCategories, BuffetCategory>> => {
  const buffetCategories = storageService.getItem<
    Record<BuffetCategories, BuffetCategory>
  >(LocalStorageItems.SortedBuffetCategories);
  if (!buffetCategories || date) {
    const { items: buffetCategoryList } =
      await apiService.getBuffetCategories();

    const offset = -(new Date().getTimezoneOffset() / 60);
    for (const category of buffetCategoryList) {
      let { items: epgList } = await apiService.getBuffetEpgByCategory(
        category.code,
        date ? date : undefined,
        offset
      );
      if (epgList && epgList.length > 0) {
        epgList = formatEpgDates<BuffetEpgItem>(epgList, offset);
        category.epgList = epgList;
        sortedBuffetCategories[category.name] = category;
      }
    }

    storageService.setItem(
      LocalStorageItems.SortedBuffetCategories,
      sortedBuffetCategories
    );
    return sortedBuffetCategories;
  }

  return buffetCategories;
};

const fetchProviderData = async (
  userDataUpdated?: boolean
): Promise<ProviderData> => {
  const providerData = storageService.getItem<ProviderData>(
    LocalStorageItems.ProviderData
  );
  if (!providerData || userDataUpdated) {
    const { items: providerList } = await apiService.getProvider();

    if (!providerList[0].colorScheme) {
      providerList[0].colorScheme = defaultProviderData.colorScheme;

      storageService.setItem(
        LocalStorageItems.ProviderData,
        providerList[0],
        0
      );
      return providerList[0];
    }

    //making sure we have all fields populated if provider did not input all colors, background image etc.
    let k: keyof ProviderColorScheme;
    for (k in providerList[0].colorScheme) {
      if (!providerList[0].colorScheme[k]) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        providerList[0].colorScheme[k] = defaultProviderData.colorScheme[k];
      }
    }

    storageService.setItem(LocalStorageItems.ProviderData, providerList[0], 0);
    return providerList[0];
  }

  return providerData;
};

const fetchUserInfo = async (userDataUpdated?: boolean): Promise<UserInfo> => {
  let userInfo = storageService.getItem<UserInfo>(LocalStorageItems.UserInfo);
  if (!userInfo || userDataUpdated) {
    userInfo = await apiService.getUserInfo();
    storageService.setItem<UserInfo>(LocalStorageItems.UserInfo, userInfo);
    return userInfo;
  }

  return userInfo;
};

const fetchAppMenuItems = async (): Promise<AppMenuItem[]> => {
  const AppMenuItemList = storageService.getItem<AppMenuItem[]>(
    LocalStorageItems.AppMenuItems
  );

  if (!AppMenuItemList) {
    const { items: appMenuItems } = await apiService.getAppMenuItems();
    storageService.setItem<AppMenuItem[]>(
      LocalStorageItems.AppMenuItems,
      appMenuItems
    );
    return appMenuItems;
  }

  return AppMenuItemList;
};

const fetchHomePageWidgets = async (): Promise<HomePageWidget[]> => {
  const HomePageWidgetList = storageService.getItem<HomePageWidget[]>(
    LocalStorageItems.HomePageWidgets
  );

  if (!HomePageWidgetList) {
    const homePageWidgets = await apiService.getHomePageWidgets();
    homePageWidgets.sort((a, b) => a.position - b.position);
    storageService.setItem<HomePageWidget[]>(
      LocalStorageItems.HomePageWidgets,
      homePageWidgets
    );
    return homePageWidgets;
  }

  return HomePageWidgetList;
};

const fetchMoviesByGenre = async (): Promise<VoDGenre[]> => {
  const moviesGenresList = storageService.getItem<VoDGenre[]>(
    LocalStorageItems.MoviesByGenre
  );

  if (!moviesGenresList) {
    const { items: movies } = await apiService.getMoviesByGenre();
    storageService.setItem<VoDGenre[]>(LocalStorageItems.MoviesByGenre, movies);
    return movies;
  }

  return moviesGenresList;
};

const fetchTvShowsByGenre = async (): Promise<VoDGenre[]> => {
  const tvShowList = storageService.getItem<VoDGenre[]>(
    LocalStorageItems.TvShowsByGenre
  );

  if (!tvShowList) {
    const { items: tvShows } = await apiService.getTvShowsByGenre();
    storageService.setItem<VoDGenre[]>(
      LocalStorageItems.TvShowsByGenre,
      tvShows
    );
    return tvShows;
  }

  return tvShowList;
};

const fetchFavoriteMovies = async (): Promise<Movie[]> => {
  const favoriteMovieList = storageService.getItem<Movie[]>(
    LocalStorageItems.FavoriteMovies
  );

  if (!favoriteMovieList) {
    const { items: movies } = await apiService.getFavoriteMovies();
    storageService.setItem<Movie[]>(LocalStorageItems.FavoriteMovies, movies);
    return movies;
  }

  return favoriteMovieList;
};

const fetchFavoriteTvShows = async (): Promise<TvShow[]> => {
  const favoriteTvShowList = storageService.getItem<TvShow[]>(
    LocalStorageItems.FavoriteTvShows
  );

  if (!favoriteTvShowList) {
    const { items: tvShows } = await apiService.getFavoriteTvShows();
    storageService.setItem<TvShow[]>(
      LocalStorageItems.FavoriteTvShows,
      tvShows
    );
    return tvShows;
  }

  return favoriteTvShowList;
};

export default {
  fetchCustomChannelList,
  fetchSortedCategories,
  fetchPromotions,
  fetchSortedBuffetCategories,
  fetchProviderData,
  fetchUserInfo,
  fetchAppMenuItems,
  fetchHomePageWidgets,
  fetchMoviesByGenre,
  fetchTvShowsByGenre,
  fetchFavoriteMovies,
  fetchFavoriteTvShows
};
